import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [newBid, setNewBid] = useState({ amount: '', delivery_days: '', proposal: '' });

  useEffect(() => {
    fetchProject();
    if (user?.id) {
      fetchBids();
    }
  }, [projectId, user]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}`);
      setProject(res.data);
    } catch (error) {
      console.error('Failed to fetch project', error);
      toast.error('Project not found');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    try {
      const res = await axios.get(`${API}/bids/project/${projectId}`);
      setBids(res.data);
    } catch (error) {
      console.error('Failed to fetch bids', error);
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bids`, {
        project_id: projectId,
        amount: parseFloat(newBid.amount),
        delivery_days: parseInt(newBid.delivery_days),
        proposal: newBid.proposal,
      });
      toast.success('Bid submitted successfully!');
      setShowBidDialog(false);
      setNewBid({ amount: '', delivery_days: '', proposal: '' });
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    }
  };

  const handleAcceptBid = async (bid) => {
    try {
      const contract = {
        type: 'project',
        project_id: projectId,
        bid_id: bid.id,
        freelancer_id: bid.freelancer_id,
        amount: bid.amount,
        delivery_days: bid.delivery_days,
      };
      const res = await axios.post(`${API}/contracts`, contract);
      toast.success('Bid accepted! Creating contract...');
      navigate(`/contracts/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to accept bid');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <Button variant="ghost" onClick={() => navigate('/projects')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
        </Button>

        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <CardTitle className="text-3xl font-heading mb-2" data-testid="project-title">{project.title}</CardTitle>
                <CardDescription>Posted by {project.client_name}</CardDescription>
              </div>
              <Badge variant={project.status === 'open' ? 'default' : 'secondary'}>{project.status}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-lg mb-3">Project Description</h3>
              <p className="text-muted-foreground leading-relaxed" data-testid="project-description">{project.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="font-heading font-semibold text-lg mb-3">Budget Range</h3>
                <div className="text-2xl font-heading font-bold text-secondary" data-testid="project-budget">
                  ₹{project.budget_min?.toLocaleString('en-IN')} - ₹{project.budget_max?.toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg mb-3">Bids Received</h3>
                <div className="text-2xl font-heading font-bold text-primary">{project.bids_count}</div>
              </div>
            </div>

            {project.skills_required.length > 0 && (
              <div className="mb-6">
                <h3 className="font-heading font-semibold text-lg mb-3">Required Skills</h3>
                <div className="flex gap-2 flex-wrap">
                  {project.skills_required.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </div>
            )}

            {user?.role === 'freelancer' && project.status === 'open' && (
              <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary text-secondary-foreground" data-testid="place-bid-button">
                    Place Your Bid
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Submit Your Bid</DialogTitle>
                    <DialogDescription>Propose your offer for this project</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmitBid} className="space-y-4" data-testid="bid-form">
                    <div>
                      <Label>Your Bid Amount (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newBid.amount}
                        onChange={(e) => setNewBid({ ...newBid, amount: e.target.value })}
                        required
                        data-testid="bid-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Delivery Time (days)</Label>
                      <Input
                        type="number"
                        value={newBid.delivery_days}
                        onChange={(e) => setNewBid({ ...newBid, delivery_days: e.target.value })}
                        required
                        data-testid="bid-delivery-input"
                      />
                    </div>
                    <div>
                      <Label>Proposal</Label>
                      <Textarea
                        value={newBid.proposal}
                        onChange={(e) => setNewBid({ ...newBid, proposal: e.target.value })}
                        required
                        rows={4}
                        data-testid="bid-proposal-input"
                        placeholder="Explain why you're the best fit for this project..."
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="bid-submit-button">
                      Submit Bid
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </CardContent>
        </Card>

        {user?.id === project.client_id && bids.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-heading">Received Bids</CardTitle>
              <CardDescription>Review and accept proposals from freelancers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {bids.map((bid) => (
                  <div key={bid.id} className="border rounded-lg p-4" data-testid={`bid-${bid.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-heading font-semibold text-lg">{bid.freelancer_name}</h3>
                        <p className="text-sm text-muted-foreground">Rating: {bid.freelancer_rating.toFixed(1)}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-heading font-bold text-secondary">₹{bid.amount?.toLocaleString('en-IN')}</div>
                        <p className="text-sm text-muted-foreground">{bid.delivery_days} days</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{bid.proposal}</p>
                    {bid.status === 'pending' && (
                      <Button
                        onClick={() => handleAcceptBid(bid)}
                        size="sm"
                        className="bg-secondary text-secondary-foreground"
                        data-testid={`accept-bid-${bid.id}`}
                      >
                        Accept Bid
                      </Button>
                    )}
                    {bid.status !== 'pending' && (
                      <Badge variant="outline">{bid.status}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
